import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IoArrowBack } from "react-icons/io5";
import { useAuthContext } from "../../../Components/AuthContext";
import { API } from "../../../api";
import { authService } from "../../../api/services/auth.service";
import { NotificationPreference, notificationService } from "../../../api/services/notification.service";
import { SITE_ID } from "../../../assets/meta/meta";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import {
  WEB_PUSH_SUPPRESSION,
  clearWebPushSuppression,
  setWebPushSuppression,
} from "../../../common/webPushPermissionPolicy.mjs";

type Section = "main" | "profile" | "password" | "notification" | "sessions" | "withdrawal";

function SectionHeader({
  onBack,
  title,
}: {
  onBack: () => void;
  title: string;
}) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
      <button
        type="button"
        onClick={onBack}
        className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
      >
        <IoArrowBack size={20} />
      </button>
      <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
        {title}
      </h1>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-gray-500 disabled:bg-gray-100"
    />
  );
}

function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-lg bg-gray-900 py-3 text-sm font-semibold text-white disabled:opacity-40"
    >
      {children}
    </button>
  );
}

// ─── Sub sections ─────────────────────────────────────────────────────────────

function ProfileSection({ userInfo, onBack }: { userInfo: any; onBack: () => void }) {
  const [name, setName] = useState<string>(userInfo?.username || "");
  const [phone, setPhone] = useState<string>(userInfo?.phone_num || "");
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning("이름을 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      await API.put("/accounts/set-username/", { name, phone_num: phone || undefined });
      toast.success("프로필이 수정되었습니다.");
      onBack();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "프로필 수정에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SectionHeader onBack={onBack} title="프로필 수정" />
      <div className="p-4">
        <Field label="이름">
          <Input value={name} onChange={setName} placeholder="이름을 입력해 주세요" />
        </Field>
        <Field label="전화번호">
          <Input value={phone} onChange={setPhone} placeholder="전화번호 (010-0000-0000)" />
        </Field>
        <PrimaryButton onClick={handleSave} disabled={submitting}>
          {submitting ? "저장 중..." : "저장"}
        </PrimaryButton>
      </div>
    </>
  );
}

function PasswordSection({ onBack }: { onBack: () => void }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!newPassword) {
      toast.warning("새 비밀번호를 입력해 주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning("비밀번호가 일치하지 않습니다.");
      return;
    }
    if (newPassword.length < 6) {
      toast.warning("비밀번호는 6자 이상이어야 합니다.");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/accounts/set-user-password/", { password: newPassword });
      toast.success("비밀번호가 변경되었습니다.");
      onBack();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "비밀번호 변경에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SectionHeader onBack={onBack} title="비밀번호 변경" />
      <div className="p-4">
        <Field label="새 비밀번호">
          <Input
            type="password"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="6자 이상 입력해 주세요"
          />
        </Field>
        <Field label="새 비밀번호 확인">
          <Input
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="비밀번호를 다시 입력해 주세요"
          />
        </Field>
        <PrimaryButton onClick={handleSave} disabled={submitting}>
          {submitting ? "변경 중..." : "변경"}
        </PrimaryButton>
      </div>
    </>
  );
}

function NotificationSection({ onBack, userId }: { onBack: () => void; userId?: number }) {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [webPushSubscribed, setWebPushSubscribed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const [preferenceResponse, webPushResponse] = await Promise.all([
          notificationService.getPreferences(),
          notificationService.getCurrentWebPushSubscription(),
        ]);
        setPreferences(preferenceResponse.data.categories);
        setWebPushSubscribed(Boolean(webPushResponse));
      } catch (err: any) {
        toast.error(err?.message || "알림 설정을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const togglePreference = async (preference: NotificationPreference) => {
    if (preference.is_mandatory || !preference.site_enabled) return;
    setSubmitting(true);
    try {
      const nextEnabled = !preference.is_enabled;
      await notificationService.updatePreference(preference.category, nextEnabled);
      setPreferences((current) => current.map((item) => (
        item.category === preference.category ? { ...item, is_enabled: nextEnabled } : item
      )));
      toast.success(nextEnabled ? "알림이 활성화되었습니다." : "알림이 비활성화되었습니다.");
    } catch (err: any) {
      toast.error("알림 설정 변경에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWebPush = async () => {
    setSubmitting(true);
    try {
      if (webPushSubscribed) {
        await notificationService.unsubscribeFromWebPush();
        setWebPushSuppression(
          window.localStorage,
          SITE_ID,
          userId,
          WEB_PUSH_SUPPRESSION.MANUAL_OFF,
        );
        setWebPushSubscribed(false);
        toast.success("Web Push 알림이 해제되었습니다.");
      } else {
        const status = await notificationService.subscribeToWebPush();
        clearWebPushSuppression(window.localStorage, SITE_ID, userId);
        setWebPushSubscribed(status.is_subscribed);
        toast.success("Web Push 알림이 활성화되었습니다.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Web Push 설정 변경에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SectionHeader onBack={onBack} title="알림 설정" />
      <div className="space-y-3 p-4">
        {loading ? (
          <p className="text-sm text-gray-500">불러오는 중...</p>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
              <div>
                <p className="text-sm font-medium text-gray-800">Web Push</p>
                <p className="text-xs text-gray-500">브라우저에서 알림을 받습니다.</p>
              </div>
              <button
                type="button"
                onClick={() => void toggleWebPush()}
                disabled={submitting || webPushSubscribed === null}
                className={`relative h-6 w-11 rounded-full transition-colors ${webPushSubscribed ? "bg-gray-900" : "bg-gray-300"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${webPushSubscribed ? "translate-x-5.5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {preferences.map((preference) => (
              <div key={preference.category} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
                <div>
                  <p className="text-sm font-medium text-gray-800">{preference.label}</p>
                  <p className="text-xs text-gray-500">
                    {preference.is_mandatory
                      ? "항상 수신되는 필수 안내입니다."
                      : !preference.site_enabled
                        ? "현재 지점에서 제공하지 않는 알림입니다."
                        : "이 지점의 알림 수신 여부를 설정합니다."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void togglePreference(preference)}
                  disabled={submitting || preference.is_mandatory || !preference.site_enabled}
                  className={`relative h-6 w-11 rounded-full transition-colors ${preference.is_enabled ? "bg-gray-900" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${preference.is_enabled ? "translate-x-5.5" : "translate-x-0.5"}`} />
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

type AuthSession = {
  id: string;
  client_source: string;
  device_name?: string;
  user_agent?: string;
  ip_address?: string;
  last_seen_at: string;
  refresh_expires_at: string;
  revoked_at?: string | null;
  is_current: boolean;
};

function SessionsSection({ onBack, logout }: { onBack: () => void; logout: () => Promise<void> }) {
  const [sessions, setSessions] = useState<AuthSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await authService.getSessions();
      setSessions(response.data?.results || []);
    } catch (err: any) {
      toast.error(err?.message || "로그인 장치를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadSessions();
  }, []);

  const handleRevoke = async (session: AuthSession) => {
    setRevokingId(session.id);
    try {
      await authService.revokeSession(session.id);
      toast.success("장치에서 로그아웃되었습니다.");
      if (session.is_current) {
        await logout();
        return;
      }
      await loadSessions();
    } catch (err: any) {
      toast.error(err?.message || "장치 로그아웃에 실패했습니다.");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <SectionHeader onBack={onBack} title="로그인한 장치" />
      <div className="space-y-3 p-4">
        {loading ? (
          <p className="text-sm text-gray-500">불러오는 중...</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500">로그인한 장치가 없습니다.</p>
        ) : (
          sessions.map((session) => (
            <div key={session.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {session.device_name || session.user_agent || "알 수 없는 장치"}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    최근 접속 {new Date(session.last_seen_at).toLocaleString()}
                  </p>
                  {session.ip_address ? (
                    <p className="mt-1 text-xs text-gray-400">IP {session.ip_address}</p>
                  ) : null}
                </div>
                {session.is_current ? (
                  <span className="rounded-full bg-gray-900 px-2 py-1 text-xs text-white">현재</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(session)}
                disabled={revokingId === session.id}
                className="w-full rounded-lg border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-40"
              >
                {revokingId === session.id ? "처리 중..." : "로그아웃"}
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function WithdrawalSection({
  onBack,
  logout,
}: {
  onBack: () => void;
  logout: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);

  const handleWithdraw = async () => {
    const ok = window.confirm(
      "탈퇴하면 모든 예약/티켓/쿠폰 내역이 삭제됩니다. 정말 탈퇴하시겠습니까?",
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      await API.delete("/accounts/deactivate-user/");
      toast.success("탈퇴 처리가 완료되었습니다.");
      await logout();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "탈퇴 처리에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SectionHeader onBack={onBack} title="회원 탈퇴" />
      <div className="p-4">
        <div className="mb-6 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <p className="mb-2 font-semibold">탈퇴 전 유의사항</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>탈퇴 시 모든 예약, 티켓, 쿠폰 내역이 영구 삭제됩니다.</li>
            <li>삭제된 데이터는 복구할 수 없습니다.</li>
            <li>진행 중인 예약이 있으면 취소 후 탈퇴해 주세요.</li>
          </ul>
        </div>
        <button
          type="button"
          onClick={handleWithdraw}
          disabled={submitting}
          className="w-full rounded-lg border border-red-500 py-3 text-sm font-semibold text-red-500 disabled:opacity-40"
        >
          {submitting ? "처리 중..." : "회원 탈퇴"}
        </button>
      </div>
    </>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

function MainMenu({
  onSelect,
  navigate,
}: {
  onSelect: (s: Section) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const items = [
    { label: "프로필 수정", section: "profile" as Section, description: "이름, 전화번호 변경" },
    { label: "알림 설정", section: "notification" as Section, description: "푸시 알림 수신 여부" },
    { label: "로그인한 장치", section: "sessions" as Section, description: "접속 장치 확인 및 로그아웃" },
    { label: "비밀번호 변경", section: "password" as Section, description: "계정 비밀번호 변경" },
    { label: "회원 탈퇴", section: "withdrawal" as Section, description: "계정 영구 삭제" },
  ];

  return (
    <>
      <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
        >
          <IoArrowBack size={20} />
        </button>
        <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
          사용자 설정
        </h1>
      </div>
      <div className="mt-2 bg-white">
        {items.map((item, idx) => (
          <button
            key={item.section}
            type="button"
            onClick={() => onSelect(item.section)}
            className={`flex w-full items-center justify-between px-4 py-4 text-left active:bg-gray-50 ${
              idx < items.length - 1 ? "border-b border-gray-50" : ""
            }`}
          >
            <div>
              <p className="text-sm font-medium text-gray-800">{item.label}</p>
              <p className="text-xs text-gray-400">{item.description}</p>
            </div>
            <IoArrowBack size={16} className="rotate-180 text-gray-300" />
          </button>
        ))}
      </div>
    </>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { userInfo, logout } = useAuthContext();
  const [section, setSection] = useState<Section>("main");

  const renderSection = () => {
    switch (section) {
      case "profile":
        return (
          <ProfileSection userInfo={userInfo} onBack={() => setSection("main")} />
        );
      case "password":
        return <PasswordSection onBack={() => setSection("main")} />;
      case "notification":
        return <NotificationSection userId={userInfo?.id} onBack={() => setSection("main")} />;
      case "sessions":
        return <SessionsSection onBack={() => setSection("main")} logout={logout} />;
      case "withdrawal":
        return <WithdrawalSection onBack={() => setSection("main")} logout={logout} />;
      default:
        return <MainMenu onSelect={setSection} navigate={navigate} />;
    }
  };

  return (
    <MobileViewContainer>
      <div className="min-h-screen bg-gray-50 pb-24">{renderSection()}</div>
    </MobileViewContainer>
  );
}
